// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, switchMap, filter, timeout, take, finalize, of, catchError, map } from 'rxjs';
import { environment } from '../environments/environment';
import { HttpClient, HttpContext } from '@angular/common/http';
import { TenantService } from './tenant.service';
import { AppInitService } from './app-init.service';
import { SKIP_LOADER } from './loader-context.token';
import {
  VerifyPinRequest,
  VerifyPinResponse,
  CurrentUserResponse,
  AuthState,
  ApiResponse,
  UserRole
} from '../models/auth.models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly endpoints = environment.apiEndpoints.auth;
  private storage = sessionStorage;

  // Local state management for authentication
  private authState$ = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
    tenantId: ''
  });

  public authState = this.authState$.asObservable();

  // Observable streams for components
  private loginSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.loginSubject.asObservable();

  private adminSubject = new BehaviorSubject<boolean>(false);
  isAdmin$ = this.adminSubject.asObservable();

  constructor(
    private http: HttpClient,
    private injector: Injector,
    private appInitService: AppInitService
  ) {
    this.initializeAuth();

  }

  /**
   * Initialize authentication state on app startup
   * Checks if user is already authenticated via cookie
   * Waits for app initialization to complete first
   */
  private initializeAuth(): void {
    // Wait for app initialization to complete
    if (!this.appInitService.isInitialized()) {
      console.warn('AppInitService not initialized yet - skipping auth check');
      return;
    }

    // Get tenant ID from app init service
    const tenantId = this.appInitService.getTenantId();

    if (tenantId) {
      this.setTenantId(tenantId);
      this.checkAuthStatus();
    }
  }

  /**
   * Verify PIN and get access to menu
   * Cookie is automatically set by backend in response
   */
  verifyPin(request: VerifyPinRequest): Observable<ApiResponse<VerifyPinResponse>> {
    return this.http.post<ApiResponse<VerifyPinResponse>>(
      `${this.apiUrl}${this.endpoints.pinVerify}`,
      request
    ).pipe(
      switchMap(response => {
        if (response.success) {
          return this.refreshAuthStatus().pipe(map(() => response));
        }
        return of(response);
      })
    );
  }

  /**
   * Verify PIN and wait for auth status to be updated
   * Returns a promise that resolves after auth state is updated
   */
  verifyPinAndWaitForAuth(request: VerifyPinRequest): Promise<boolean> {
    return this.verifyPin(request).pipe(
      // Catch HTTP errors (401, 400, 429) and return false
      catchError((error) => {
        // PIN verification failed - return false immediately
        console.error('PIN verification failed:', error);
        return of(false);
      }),
      // Check if PIN verification was successful before proceeding
      switchMap((response) => {
        // If response is boolean false (from catchError), return false immediately
        if (typeof response === 'boolean' && !response) {
          return of(false);
        }
        // If response is an ApiResponse but not successful, return false
        if (typeof response === 'object' && response && 'success' in response && !response.success) {
          return of(false);
        }
        // PIN verification succeeded, wait for auth status check to complete
        return this.authState.pipe(
          // Filter only authenticated states
          filter(state => state.isAuthenticated),
          // Take first authenticated state
          take(1),
          // Timeout after 5 seconds to prevent hanging
          timeout(5000),
          // Map to true when auth state is confirmed
          switchMap(() => of(true))
        );
      }),
      // Catch timeout errors when waiting for auth state
      catchError((error) => {
        // Timeout waiting for auth state update
        console.warn('Timeout waiting for auth state update:', error);
        return of(false);
      }),
      // Convert to promise
      finalize(() => {
        // Ensure cleanup happens
      })
    ).toPromise()
      .then((result) => result ?? false);
  }

  /**
   * Initiate Google OAuth login
   */
  googleLogin(clientId: string, returnUrl: string = '/'): void {
    const googleLoginUrl = `${this.apiUrl}${this.endpoints.googleLogin}`;
    const params = new URLSearchParams({
      clientId: clientId,
      returnUrl: returnUrl,
      host: window.location.origin
    });

    window.location.href = `${googleLoginUrl}?${params.toString()}`;
  }

  /**
   * Check current authentication status
   * Calls GET /api/auth/me endpoint
   * Uses cookie automatically via interceptor
   */
  checkAuthStatus(): void {
    this.refreshAuthStatus().subscribe();
  }

  /**
   * Refreshes the auth status and returns an observable of the result
   */
  refreshAuthStatus(): Observable<AuthState> {
    const state = this.authState$.value;

    return this.http.get<ApiResponse<CurrentUserResponse>>(
      `${this.apiUrl}${this.endpoints.currentUser}`,
      {
        context: new HttpContext().set(SKIP_LOADER, true)
      }
    ).pipe(
      timeout(10000), // Add timeout to prevent hanging subscriptions
      map((response) => {
        if (response.success && response.value) {
          const user = response.value;
          const newState: AuthState = {
            isAuthenticated: true,
            user: user,
            loading: false,
            error: null,
            tenantId: user.tenantId
          };
          this.authState$.next(newState);
          this.setRoles(user.roles);
          return newState;
        } else {
          // Successful response but not authenticated
          const newState: AuthState = {
            ...state,
            isAuthenticated: false,
            user: null,
            loading: false,
            error: 'Invalid response from server'
          };
          this.authState$.next(newState);
          this.setRoles([]);
          return newState;
        }
      }),
      catchError((error) => {
        // Not authenticated or network error
        console.warn('Auth status check failed:', error);
        const newState: AuthState = {
          ...state,
          isAuthenticated: false,
          user: null,
          loading: false,
          error: error.message || 'Authentication check failed'
        };
        this.authState$.next(newState);
        this.setRoles([]);
        return of(newState);
      }),
      finalize(() => {
        // Ensure we set loading to false when complete
        const currentState = this.authState$.value;
        if (currentState.loading) {
          this.authState$.next({
            ...currentState,
            loading: false
          });
        }
      })
    );
  }

  /**
   * Logout - clears cookie on backend
   */
  logout(): Observable<any> {
    return this.http.post(`${this.apiUrl}${this.endpoints.logout}`, {}).pipe(
      tap(() => {
        // Clear local auth state
        this.authState$.next({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: null,
          tenantId: ''
        });

        // Only clear auth-related items, don't clear all sessionStorage
        sessionStorage.removeItem('returnUrl');
        sessionStorage.removeItem('tenantId');

        this.setRoles([]);
      }),
      finalize(() => {
        // Ensure cleanup happens regardless of success/failure
      })
    );
  }

  /**
   * Get current user info from local state
   */
  getCurrentUser(): CurrentUserResponse | null {
    return this.authState$.value.user;
  }

  /**
   * Get current authentication status
   */
  isAuthenticated(): boolean {
    return this.authState$.value.isAuthenticated;
  }

  /**
   * Get current tenant ID
   */
  getCurrentTenantId(): string {
    return this.authState$.value.tenantId;
  }

  /**
   * Get authorized menus
   */
  getAuthorizedMenus(): string[] {
    return this.authState$.value.user?.authorizedMenus || [];
  }

  /**
   * Get user roles
   */
  getUserRoles(): string[] {
    return this.authState$.value.user?.roles || [];
  }

  /**
   * Set tenant ID (called during initialization or on tenant change)
   */
  private setTenantId(tenantId: string): void {
    sessionStorage.setItem('tenantId', tenantId);
    const state = this.authState$.value;
    this.authState$.next({ ...state, tenantId });
  }

  /**
   * Set roles and update role observables
   */
  private setRoles(roles: string[]): void {
    const rolesLower = roles.map(r => r.toLowerCase());
    // Use UserRole enum for type-safe role checking
    const isLoggedIn = rolesLower.includes(UserRole.LoggedInUser) || rolesLower.includes(UserRole.Admin);
    const isAdmin = rolesLower.includes(UserRole.Admin);

    this.loginSubject.next(isLoggedIn);
    this.adminSubject.next(isAdmin);
  }

  /**
   * Get legacy isLoggedIn property for backward compatibility
   */
  get isLoggedIn(): boolean {
    return this.loginSubject.value;
  }

  /**
   * Get legacy isAdmin property for backward compatibility
   */
  get isAdmin(): boolean {
    return this.adminSubject.value;
  }

  /**
   * Set return URL for redirect after login
   */
  setReturnUrl(url: string): void {
    sessionStorage.setItem('returnUrl', url);
  }

  /**
   * Get return URL
   */
  getReturnUrl(): string {
    return sessionStorage.getItem('returnUrl') || '/';
  }

  /**
   * Check if user has access to a specific menu
   */
  hasAccessToMenu(menuKey: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Admin can access all menus
    if (this.isAdmin) {
      return true;
    }

    // Check if menu is in authorized menus
    return user.authorizedMenus.includes(menuKey);
  }

  /**
   * DEPRECATED: Backward compatibility - use getCurrentUser() instead
   * Get current user from auth state
   */
  get currentUser(): CurrentUserResponse | null {
    return this.getCurrentUser();
  }

  /**
   * DEPRECATED: Backward compatibility - use loginWithGoogle() instead
   * Legacy method for admin login (not used in new flow)
   */
  async loginWithGoogle(redirectUrl: string): Promise<void> {
    const tenantService = this.injector.get(TenantService);
    const tenantId = await tenantService.getTenantId();
    const host = `${location.origin}/${tenantService.getTenantKey()}/login`;
    window.location.href = `${environment.apiUrl}/auth/google-login?host=${host}&returnUrl=${redirectUrl}&clientId=${tenantId}`;
  }

  /**
   * DEPRECATED: Backward compatibility - login method
   * Not used in new HttpOnly cookie flow
   */
  login(credentials: { username: string, password: string }): Observable<any> {
    // This method is kept for backward compatibility with admin login
    // In new flow, use PIN verification or Google OAuth instead
    return new Observable(observer => {
      observer.error({ message: 'Login method deprecated. Use PIN verification or Google OAuth.' });
    });
  }

  /**
   * DEPRECATED: Backward compatibility - menu token checking
   * Returns false since we use cookies now
   */
  hasMenuToken(menuKey: string): boolean {
    // In new flow, menu validation is done via authorizedMenus in CurrentUserResponse
    return this.hasAccessToMenu(menuKey);
  }

  /**
   * DEPRECATED: Backward compatibility - menu token setting
   * No-op in new flow since cookies are handled by browser
   */
  setCurrentMenuKey(menuKey: string | null): void {
    // No longer needed - kept for backward compatibility
  }

  /**
   * DEPRECATED: Backward compatibility - get current menu key
   * Always returns null since we don't track menu context
   */
  getCurrentMenuKey(): string | null {
    // No longer tracked - kept for backward compatibility
    return null;
  }

  /**
   * DEPRECATED: Backward compatibility - get menus with tokens
   * Returns authorized menus instead
   */
  getMenusWithTokens(): string[] {
    return this.getAuthorizedMenus();
  }

  /**
   * Logout from a specific menu - delegates to the main logout() method
   * Performs complete logout with backend signout
   */
  logoutFromMenu(menuKey: string): Observable<any> {
    return this.logout();
  }
}
